import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminGetMe, adminLogout } from '../../lib/api';
import { useAdminStats, useAdminPurchases, useAdminSubscribers, useAdminCodes } from '../../hooks/useAdmin';

type Tab = 'purchases' | 'subscribers' | 'codes';

export function Dashboard() {
  const [adminEmail, setAdminEmail] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('purchases');
  const navigate = useNavigate();

  useEffect(() => {
    adminGetMe()
      .then((me) => setAdminEmail(me.email))
      .catch(() => navigate('/admin/login'));
  }, [navigate]);

  const handleLogout = async () => {
    await adminLogout();
    navigate('/admin/login');
  };

  if (!adminEmail) return null;

  return (
    <div className="min-h-screen bg-bg">
      {/* Top bar */}
      <header className="bg-white border-b border-sage/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <h1 className="font-serif text-lg font-semibold text-text">Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-text-light">{adminEmail}</span>
            <button
              type="button"
              onClick={handleLogout}
              className="text-sm text-text-light hover:text-red-600 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <StatsCards />

        {/* Tabs */}
        <div className="mt-8 border-b border-sage/10">
          <nav className="flex gap-6">
            {(['purchases', 'subscribers', 'codes'] as Tab[]).map((tab) => (
              <button
                type="button"
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 text-sm font-medium capitalize transition-colors border-b-2 ${
                  activeTab === tab
                    ? 'border-sage text-sage-dark'
                    : 'border-transparent text-text-light hover:text-text'
                }`}
              >
                {tab === 'codes' ? 'Redemption Codes' : tab}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-6">
          {activeTab === 'purchases' && <PurchasesTable />}
          {activeTab === 'subscribers' && <SubscribersTable />}
          {activeTab === 'codes' && <CodesTable />}
        </div>
      </main>
    </div>
  );
}

// --- Stats Cards ---

function StatsCards() {
  const { stats, loading } = useAdminStats();

  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static list, index is stable
          <div key={i} className="bg-white rounded-xl border border-sage/10 p-6 animate-pulse">
            <div className="h-4 bg-sage/10 rounded w-20 mb-3" />
            <div className="h-8 bg-sage/10 rounded w-16" />
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    { label: 'Total Revenue', value: `$${(stats.totalRevenue / 100).toFixed(2)}`, color: 'text-sage-dark' },
    { label: 'Purchases', value: stats.totalPurchases.toString(), color: 'text-text' },
    { label: 'Subscribers', value: stats.totalSubscribers.toString(), color: 'text-text' },
    { label: 'Codes Available', value: `${stats.availableCodes} / ${stats.totalCodes}`, color: 'text-text' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, i) => (
        <motion.div
          key={card.label}
          className="bg-white rounded-xl border border-sage/10 p-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05 }}
        >
          <p className="text-sm text-text-light">{card.label}</p>
          <p className={`text-2xl font-bold mt-1 font-serif ${card.color}`}>{card.value}</p>
        </motion.div>
      ))}
    </div>
  );
}

// --- Pagination ---

function Pagination({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (p: number) => void }) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between mt-4 text-sm">
      <p className="text-text-light">
        Page {page} of {totalPages}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="px-3 py-1.5 rounded-lg border border-sage/20 text-text-light hover:bg-sage/5 disabled:opacity-40 transition"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="px-3 py-1.5 rounded-lg border border-sage/20 text-text-light hover:bg-sage/5 disabled:opacity-40 transition"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// --- Purchases Table ---

function PurchasesTable() {
  const { data, loading, loadPage } = useAdminPurchases();

  if (loading && !data) return <TableSkeleton cols={5} />;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-text-light border-b border-sage/10">
              <th className="pb-3 pr-4 font-medium">Buyer</th>
              <th className="pb-3 pr-4 font-medium">Amount</th>
              <th className="pb-3 pr-4 font-medium">Status</th>
              <th className="pb-3 pr-4 font-medium">Downloads</th>
              <th className="pb-3 font-medium">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sage/5">
            {data?.data.map((p) => (
              <tr key={p.id} className="hover:bg-sage/5 transition-colors">
                <td className="py-3 pr-4 text-text">{p.email ?? '—'}</td>
                <td className="py-3 pr-4 text-text">${(p.amountCents / 100).toFixed(2)}</td>
                <td className="py-3 pr-4">
                  <StatusBadge status={p.status} />
                </td>
                <td className="py-3 pr-4 text-text-light">{p.downloadCount} / {p.maxDownloads}</td>
                <td className="py-3 text-text-light">{formatDate(p.createdAt)}</td>
              </tr>
            ))}
            {data?.data.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-text-light">No purchases yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {data && <Pagination page={data.page} totalPages={data.totalPages} onPageChange={loadPage} />}
    </div>
  );
}

// --- Subscribers Table ---

function SubscribersTable() {
  const { data, loading, loadPage } = useAdminSubscribers();

  if (loading && !data) return <TableSkeleton cols={4} />;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-text-light border-b border-sage/10">
              <th className="pb-3 pr-4 font-medium">Email</th>
              <th className="pb-3 pr-4 font-medium">Source</th>
              <th className="pb-3 pr-4 font-medium">Status</th>
              <th className="pb-3 font-medium">Subscribed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sage/5">
            {data?.data.map((s) => (
              <tr key={s.id} className="hover:bg-sage/5 transition-colors">
                <td className="py-3 pr-4 text-text">{s.email}</td>
                <td className="py-3 pr-4 text-text-light">{s.source.replace('_', ' ')}</td>
                <td className="py-3 pr-4">
                  <StatusBadge status={s.status} />
                </td>
                <td className="py-3 text-text-light">{formatDate(s.subscribedAt)}</td>
              </tr>
            ))}
            {data?.data.length === 0 && (
              <tr><td colSpan={4} className="py-8 text-center text-text-light">No subscribers yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {data && <Pagination page={data.page} totalPages={data.totalPages} onPageChange={loadPage} />}
    </div>
  );
}

// --- Codes Table ---

function CodesTable() {
  const { data, loading, loadPage } = useAdminCodes();

  if (loading && !data) return <TableSkeleton cols={5} />;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-text-light border-b border-sage/10">
              <th className="pb-3 pr-4 font-medium">Code</th>
              <th className="pb-3 pr-4 font-medium">Assigned To</th>
              <th className="pb-3 pr-4 font-medium">Status</th>
              <th className="pb-3 pr-4 font-medium">Downloads</th>
              <th className="pb-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sage/5">
            {data?.data.map((c) => (
              <tr key={c.id} className="hover:bg-sage/5 transition-colors">
                <td className="py-3 pr-4 font-mono text-text tracking-wider">{c.code}</td>
                <td className="py-3 pr-4 text-text-light">{c.assignedEmail ?? '—'}</td>
                <td className="py-3 pr-4">
                  <StatusBadge status={c.redeemedAt ? 'redeemed' : 'available'} />
                </td>
                <td className="py-3 pr-4 text-text-light">{c.downloadCount}</td>
                <td className="py-3 text-text-light">{formatDate(c.createdAt)}</td>
              </tr>
            ))}
            {data?.data.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-text-light">No codes yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {data && <Pagination page={data.page} totalPages={data.totalPages} onPageChange={loadPage} />}
    </div>
  );
}

// --- Shared helpers ---

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    completed: 'bg-sage/10 text-sage-dark',
    active: 'bg-sage/10 text-sage-dark',
    available: 'bg-sage/10 text-sage-dark',
    pending: 'bg-yellow-50 text-yellow-700',
    failed: 'bg-red-50 text-red-700',
    refunded: 'bg-red-50 text-red-700',
    redeemed: 'bg-pink-light text-text',
    unsubscribed: 'bg-gray-100 text-gray-600',
  };

  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${styles[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function TableSkeleton({ cols }: { cols: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static list, index is stable
        <div key={i} className="flex gap-4 animate-pulse">
          {Array.from({ length: cols }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static list, index is stable
            <div key={j} className="h-5 bg-sage/10 rounded flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
